import { jsPDF } from 'jspdf';
import autoTable, { UserOptions } from 'jspdf-autotable';

import Inter from './fonts/Inter-Regular.ttf';
import InterBold from './fonts/Inter-SemiBold.ttf';

interface IInsertTableOptions extends UserOptions {
  emptyDataPlaceholder?: string;
}

interface IInsertTextOptions {
  text: string;
  isTitle?: boolean;
}

export class PDFGenerator {
  protected pdf: jsPDF;
  protected gap: number;
  protected padding: number;
  protected footer: HTMLElement | null;

  private currentY: number;
  private pageMargin: {
    top: number;
    bottom: number;
    left: number;
  };

  constructor(footer?: HTMLElement) {
    this.gap = 16;
    this.padding = 16;

    const margin = 32;
    const bottomMargin = footer
      ? footer.clientHeight + margin + this.gap
      : margin;

    this.pageMargin = {
      top: margin,
      left: margin,
      bottom: bottomMargin,
    };
    this.currentY = this.pageMargin.top;

    this.footer = footer ?? null;

    this.pdf = new jsPDF({ unit: 'px', hotfixes: ['px_scaling'] });

    this.applyFonts();

    this.pdf.internal.events.subscribe('addPage', () => {
      this.currentY = this.pageMargin.top;
    });
  }

  public addPage = (): void => {
    this.pdf.addPage();
  };

  private applyFonts = (): void => {
    this.pdf.setLanguage('ru');

    this.pdf.addFont(Inter, 'Inter', 'normal');
    this.pdf.addFont(InterBold, 'InterBold', 'normal');
  };

  public insertText = ({ text, isTitle }: IInsertTextOptions): void => {
    const fontSize = isTitle ? 10 : 8;
    const textFontName = isTitle ? 'InterBold' : 'Inter';
    const currentFontName = this.pdf.getFont().fontName;

    if (currentFontName !== textFontName) {
      this.pdf.setFont(textFontName);
      this.pdf.setTextColor('#0D1217');
    }

    this.pdf.setFontSize(fontSize);

    this.currentY = this.currentY + this.padding;

    const textLines: Array<string> = this.pdf.splitTextToSize(
      text,
      this.pdf.internal.pageSize.getWidth() -
        this.pageMargin.left * 2 -
        (isTitle ? 0 : this.padding * 2)
    );

    this.pdf.text(
      textLines,
      this.pageMargin.left + (isTitle ? 0 : this.padding),
      this.currentY,
      {
        baseline: 'middle',
      }
    );

    this.currentY =
      this.currentY + textLines.length * this.pdf.getLineHeight() + 8;

    if (isTitle) {
      this.drawLine();
    }

    this.currentY = this.currentY + this.gap;
  };

  private drawLine = (): void => {
    this.pdf.setDrawColor('black');
    const startX = this.pageMargin.left;
    const startY = this.currentY;

    const endX = this.pdf.internal.pageSize.getWidth() - this.pageMargin.left;
    const endY = this.currentY;

    this.pdf.line(startX, startY, endX, endY);
  };

  public insertHTML = async (html: HTMLElement): Promise<void> => {
    const blockHeight = html.clientHeight;

    const pageNumber = this.pdf.getCurrentPageInfo().pageNumber;
    const pageHeight =
      this.pdf.internal.pageSize.getHeight() -
      this.pageMargin.top -
      this.pageMargin.bottom;

    const canvasHeight = pageHeight * pageNumber;

    if (
      blockHeight + this.currentY + pageHeight * (pageNumber - 1) >
      canvasHeight
    ) {
      this.addPage();

      return this.insertHTML(html);
    }

    return this.pdf
      .html(html, {
        x: this.pageMargin.left,
        y:
          this.pdf.internal.pageSize.getHeight() * (pageNumber - 1) +
          this.currentY,
      })
      .then(() => {
        this.currentY = this.currentY + blockHeight + this.gap;
      });
  };

  public insertTable = async (
    options: IInsertTableOptions = {}
  ): Promise<void> => {
    if (options.body?.length) {
      autoTable(this.pdf, {
        ...options,
        startY: this.currentY,
        theme: 'plain',
        styles: {
          font: 'Inter',
          fontSize: 8,
          cellPadding: { horizontal: 8, vertical: 4 },
          minCellHeight: 20,
        },
        headStyles: {
          textColor: '#6E7174',
          cellPadding: { vertical: 6, horizontal: 8 },
        },
        margin: { horizontal: 32, bottom: this.pageMargin.bottom },
      });

      // @ts-ignore was extended with auto-table module
      this.currentY = this.pdf.lastAutoTable.finalY + this.gap;

      return;
    }

    if (options.emptyDataPlaceholder) {
      await this.insertText({
        text: options.emptyDataPlaceholder,
      });
    }
  };

  private appendFooter = async (): Promise<void> => {
    if (this.footer) {
      const pages = this.pdf.getNumberOfPages();

      for (let i = 1; i <= pages; i++) {
        this.pdf.setPage(i);

        await this.pdf.html(this.footer, {
          x: this.pageMargin.left,
          y:
            this.pdf.internal.pageSize.getHeight() * i -
            this.pageMargin.bottom -
            this.gap +
            this.footer.clientHeight,
        });
      }
    }
  };

  public save = async (fileName: string): Promise<void> => {
    await this.appendFooter();

    this.pdf.save(fileName);
  };

  public getPDFUrl = async (): Promise<URL> => {
    await this.appendFooter();

    return this.pdf.output('bloburi');
  };
}
